/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {groupSensorData} from '@ion/core/src/entities/GenericSensorUtils';
import {error} from '@ion/core/src/logger/logger';
import {getAssetEventsByTimeRange} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseGenericSensor = UseDefaultQueryResult & {
  genericSensorData: GenericObject;
};

export interface UseGenericSensorParams {
  deviceEsn: string;
  enabled?: boolean;
  staleTime?: number | DurationResult;
};

/**
 * @useGenericSensor
 *
 * Provides a hook to fetch generic sensor data for a given device then
 * groups the data by sensor type.
 *
 * ```typescript
 *
 * import {useGenericSensor} from '@ion/query/src/hooks/';
 *
 * const {genericSensorData, isLoading} = useGenericSensor({
 *  deviceEsn : '1234567890'
 * });
 *
 * ```
 *
 * @param deviceEsn - The device ESN to fetch data for.
 * @param enabled - Whether or not the query should be enabled.
 * @param staleTime - The stale time for the query.
 *
 */
export const useGenericSensor = ({
  deviceEsn,
  enabled = false,
  staleTime = getStaleTime(1, false, false)
}: UseGenericSensorParams): UseGenericSensor => {
  const query = useQuery([QueryKey.GENERIC_SENSOR_DATA, deviceEsn], async() => {
    try {
      const sensorData = await getAssetEventsByTimeRange({
        deviceEsn : deviceEsn
      });
      return sensorData;
    } catch (err) {
      error('There was an error fetching the generic sensor data:', err);
    }
  }, {
    staleTime : staleTime as number,
    enabled
  });

  return createQueryResult(query, {
    genericSensorData : groupSensorData(query.data?.results)
  }) as UseGenericSensor;
};

export default useGenericSensor;
